import React from "react";

import {
  GREEN,
  PAYMENT_LABELS,
  STATUS_META,
  formatClock,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPaymentMode,
  getReservationNo,
  normalizeType,
} from "./reservationViewUtils";

/**
 * Print-only invoice. It is rendered outside the dashboard (see printDocument)
 * so it carries its own inline styles and uses no icons, bootstrap classes or
 * interactive controls.
 */

const INK = "#212529";
const SOFT_INK = "#6b7280";
const LINE = "#d7dbdf";

const page = {
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: 12,
  color: INK,
  lineHeight: 1.45,
};

const label = { color: SOFT_INK, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 };
const soft = { color: SOFT_INK, fontSize: 11 };
const th = {
  background: GREEN,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  textAlign: "left",
  padding: "7px 8px",
  border: `1px solid ${GREEN}`,
};
const td = { padding: "7px 8px", border: `1px solid ${LINE}`, verticalAlign: "top" };
const tdRight = { ...td, textAlign: "right", whiteSpace: "nowrap" };

const Field = ({ title, children }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={label}>{title}</div>
    <div>{children || "-"}</div>
  </div>
);

const TotalRow = ({ title, value, strong, top, tone }) => (
  <tr>
    <td
      style={{
        padding: "5px 10px 5px 0",
        borderTop: top ? `1px solid ${LINE}` : "none",
        fontWeight: strong ? 700 : 400,
        color: tone || INK,
      }}
    >
      {title}
    </td>
    <td
      style={{
        padding: "5px 0",
        borderTop: top ? `1px solid ${LINE}` : "none",
        textAlign: "right",
        whiteSpace: "nowrap",
        fontWeight: strong ? 700 : 400,
        color: tone || INK,
      }}
    >
      {value}
    </td>
  </tr>
);

const SignatureBox = ({ title }) => (
  <div style={{ width: 210, textAlign: "center" }}>
    <div style={{ borderTop: `1px solid ${INK}`, paddingTop: 6, fontWeight: 600 }}>{title}</div>
    <div style={{ ...soft, marginTop: 2 }}>Name & Date</div>
  </div>
);

const stayDate = (date, time) => {
  const day = formatDate(date);
  const clock = formatClock(time);
  return clock ? `${day} · ${clock}` : day;
};

const guestCount = (reservation) =>
  [
    `${Number(reservation.adults || 0)} adult(s)`,
    Number(reservation.children || 0) ? `${Number(reservation.children)} child(ren)` : "",
    Number(reservation.pets || 0) ? `${Number(reservation.pets)} pet(s)` : "",
  ]
    .filter(Boolean)
    .join(", ");

const ReservationInvoiceDocument = ({
  reservation,
  hotel,
  hotelImage = "",
  money,
  payments = {},
  settlement = {},
  addedServices = [],
  nights = 0,
  printedAt,
}) => {
  const statusMeta = STATUS_META[normalizeType(reservation.reservationType)];
  const guestName =
    [reservation.firstName, reservation.lastName].filter(Boolean).join(" ") || "-";
  const rooms = reservation.roomDetails || [];

  const hotelAddress = [hotel?.address, hotel?.city || reservation.city]
    .filter(Boolean)
    .join(", ");
  const hotelContact = [hotel?.contactNumber, hotel?.email].filter(Boolean).join(" · ");

  const paymentRows = ["advance", "installment", "final"]
    .map((key) => ({ key, ...(payments[key] || {}) }))
    .filter((row) => Number(row.amount || 0) > 0);
  const refund = Number(settlement?.amount || 0);
  const hasRefund = refund > 0;
  const remaining = money.remainingBalance;

  return (
    <div style={page}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 20,
          borderBottom: `2px solid ${GREEN}`,
          paddingBottom: 12,
        }}
      >
        <div style={{ display: "flex", gap: 12 }}>
          {hotelImage ? (
            <img
              src={hotelImage}
              alt=""
              style={{ width: 76, height: 76, objectFit: "cover", borderRadius: 6 }}
            />
          ) : null}
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {hotel?.name || reservation.hotelName || "-"}
            </div>
            {hotelAddress ? <div style={soft}>{hotelAddress}</div> : null}
            {hotelContact ? <div style={soft}>{hotelContact}</div> : null}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 21, fontWeight: 700, color: GREEN, letterSpacing: 1.5 }}>
            INVOICE
          </div>
          <div style={{ fontWeight: 600 }}>{getReservationNo(reservation)}</div>
          <div style={soft}>Issued {formatDateTime(printedAt)}</div>
          <div
            style={{
              display: "inline-block",
              marginTop: 4,
              padding: "2px 10px",
              borderRadius: 999,
              background: statusMeta.background,
              color: statusMeta.color,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {statusMeta.label}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 24, marginTop: 14 }}>
        <div style={{ flex: 1.2 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Billed To</div>
          <div style={{ fontWeight: 600 }}>{guestName}</div>
          {reservation.mobile ? <div style={soft}>{reservation.mobile}</div> : null}
          {reservation.email ? <div style={soft}>{reservation.email}</div> : null}
          {reservation.passport ? (
            <div style={soft}>CNIC / Passport: {reservation.passport}</div>
          ) : null}
          {reservation.address ? <div style={soft}>{reservation.address}</div> : null}
        </div>
        <div style={{ flex: 1 }}>
          <Field title="Check-in">{stayDate(reservation.checkIn, reservation.checkInTime)}</Field>
          <Field title="Check-out">
            {stayDate(reservation.checkOut, reservation.checkOutTime)}
          </Field>
          <Field title="Nights">{nights ? `${nights} night(s)` : "-"}</Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field title="Booked On">{formatDate(reservation.bookingDate)}</Field>
          <Field title="Booking Source">{reservation.source}</Field>
          <Field title="Guests">{guestCount(reservation)}</Field>
        </div>
      </div>

      <div style={{ fontWeight: 700, margin: "16px 0 6px" }}>Charges</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 28, textAlign: "center" }}>#</th>
            <th style={th}>Description</th>
            <th style={{ ...th, width: 42, textAlign: "center" }}>Qty</th>
            <th style={{ ...th, width: 88, textAlign: "right" }}>Rate</th>
            <th style={{ ...th, width: 78, textAlign: "right" }}>Tax</th>
            <th style={{ ...th, width: 96, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rooms.map((room, index) => (
            <tr key={`room-${index}`}>
              <td style={{ ...td, textAlign: "center" }}>{index + 1}</td>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>
                  {room.name || "Room"}
                  {room.planName ? ` (${room.planName})` : ""}
                </div>
                <div style={soft}>
                  {[
                    nights ? `${nights} night(s)` : "",
                    room.guests ? `${room.guests} guest(s)` : "",
                    (room.physicalRooms || []).length
                      ? `Room ${(room.physicalRooms || [])
                          .map((unit) => unit?.label || unit?.id || unit)
                          .join(", ")}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </td>
              <td style={{ ...td, textAlign: "center" }}>{room.quantity || 1}</td>
              <td style={tdRight}>{formatMoney(room.rate?.[0]?.ratePerNight)}</td>
              <td style={tdRight}>{formatMoney(room.roomTotalTax)}</td>
              <td style={tdRight}>
                {formatMoney(
                  Number(room.roomTotalPrice || 0) + Number(room.roomTotalTax || 0)
                )}
              </td>
            </tr>
          ))}

          {addedServices.map((service, index) => (
            <tr key={`service-${index}`}>
              <td style={{ ...td, textAlign: "center" }}>{rooms.length + index + 1}</td>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>
                  {service.item}
                  {service.type ? ` (${service.type})` : ""}
                </div>
                {service.desc ? <div style={soft}>{service.desc}</div> : null}
              </td>
              <td style={{ ...td, textAlign: "center" }}>{service.quantity}</td>
              <td style={tdRight}>{formatMoney(service.rate)}</td>
              <td style={tdRight}>{formatMoney(service.tax)}</td>
              <td style={tdRight}>
                {formatMoney(
                  Number(service.quantity || 0) * Number(service.rate || 0) +
                    Number(service.tax || 0)
                )}
              </td>
            </tr>
          ))}

          {!rooms.length && !addedServices.length ? (
            <tr>
              <td style={{ ...td, textAlign: "center", color: SOFT_INK }} colSpan={6}>
                No charges recorded
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <table style={{ width: 300, borderCollapse: "collapse" }}>
          <tbody>
            <TotalRow title="Room Charges" value={formatMoney(money.roomTotal)} />
            <TotalRow title="Total Tax" value={formatMoney(money.taxes)} />
            <TotalRow title="Total Services" value={formatMoney(money.services)} />
            <TotalRow
              title="Total Amount"
              value={formatMoney(money.amountCharged)}
              strong
              top
            />
            <TotalRow title="Collected Amount" value={formatMoney(money.collected)} />
            {hasRefund ? (
              <TotalRow title={PAYMENT_LABELS.settlement} value={`- ${formatMoney(refund)}`} />
            ) : null}
            <TotalRow
              title={remaining < 0 ? "Refund Due" : "Remaining"}
              value={formatMoney(Math.abs(remaining))}
              strong
              top
              tone={remaining === 0 ? GREEN : "#c0392b"}
            />
          </tbody>
        </table>
      </div>

      <div style={{ fontWeight: 700, margin: "14px 0 6px" }}>Payments</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Type</th>
            <th style={{ ...th, width: 110 }}>Mode</th>
            <th style={{ ...th, width: 150 }}>Deposited At</th>
            <th style={th}>Remarks</th>
            <th style={{ ...th, width: 96, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {paymentRows.map((row) => (
            <tr key={row.key}>
              <td style={td}>{PAYMENT_LABELS[row.key]}</td>
              <td style={td}>{formatPaymentMode(row.mode)}</td>
              <td style={td}>{formatDateTime(row.recordedAt)}</td>
              <td style={td}>{row.remarks || "-"}</td>
              <td style={tdRight}>{formatMoney(row.amount)}</td>
            </tr>
          ))}
          {hasRefund ? (
            <tr>
              <td style={td}>{PAYMENT_LABELS.settlement}</td>
              <td style={td}>{formatPaymentMode(settlement.mode)}</td>
              <td style={td}>{formatDateTime(settlement.recordedAt)}</td>
              <td style={td}>{settlement.remarks || "-"}</td>
              <td style={tdRight}>- {formatMoney(refund)}</td>
            </tr>
          ) : null}
          {!paymentRows.length && !hasRefund ? (
            <tr>
              <td style={{ ...td, textAlign: "center", color: SOFT_INK }} colSpan={5}>
                No payments recorded
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 64,
          paddingTop: 4,
        }}
      >
        <SignatureBox title="Guest" />
        <SignatureBox title="Cashier" />
      </div>

      <div
        style={{
          ...soft,
          marginTop: 22,
          paddingTop: 8,
          borderTop: `1px solid ${LINE}`,
          textAlign: "center",
        }}
      >
        Computer generated invoice for {getReservationNo(reservation)} · Thank you for staying
        with us.
      </div>
    </div>
  );
};

export default ReservationInvoiceDocument;
